import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Moon, Sun, Globe } from "lucide-react";
import { useTheme } from "@/contexts/ThemeContext";
import { useLang } from "@/contexts/LanguageContext";

export default function Navbar() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [langOpen, setLangOpen] = useState(false);
    const loc = useLocation();
    const { theme, toggle } = useTheme();
    const { lang, setLang, t } = useLang();

    const links = [
        { to: "/", label: t("nav.home"), key: "home" },
        { to: "/about", label: t("nav.about"), key: "about" },
        { to: "/services", label: t("nav.services"), key: "services" },
        { to: "/free-kundli", label: "Free Kundli", key: "free-kundli" },
        { to: "/daily-horoscope", label: "Horoscope", key: "horoscope" },
        { to: "/courses", label: t("nav.courses"), key: "courses" },
        { to: "/blog", label: t("nav.blog"), key: "blog" },
        { to: "/contact", label: t("nav.contact"), key: "contact" },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => { setOpen(false); setLangOpen(false); }, [loc.pathname]);

    const pickLang = (l) => { setLang(l); setLangOpen(false); };

    return (
        <header
            data-testid="navbar"
            className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
                scrolled ? "bg-ink/85 backdrop-blur-xl border-b border-gold/10" : "bg-transparent"
            }`}
        >
            <div className="container-vv flex h-20 items-center justify-between">
                <Link to="/" className="flex items-center gap-3 group" data-testid="brand-link">
                    <div className="relative h-12 w-12 rounded-full overflow-hidden border border-gold/40 bg-cream group-hover:border-gold transition-colors flex-shrink-0">
                        <img src="https://customer-assets.emergentagent.com/job_dr-tannu-vedic/artifacts/ad62uzli_IMG_3335.png" alt="VedicByTannu logo" className="w-full h-full object-cover" />
                    </div>
                    <div className="leading-tight">
                        <div className="font-serif text-lg text-cream tracking-wide">VedicByTannu</div>
                        <div className="text-[10px] tracking-[0.22em] uppercase text-gold/80">{t("hero.tagline")}</div>
                    </div>
                </Link>

                <nav className="hidden lg:flex items-center gap-9">
                    {links.map((l) => (
                        <NavLink
                            key={l.key}
                            to={l.to}
                            data-testid={`nav-${l.key}`}
                            className={({ isActive }) =>
                                `text-sm tracking-wide transition-colors ${
                                    isActive ? "text-gold" : "text-cream hover:text-gold"
                                }`
                            }
                            end={l.to === "/"}
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    {/* Language toggle */}
                    <div className="relative">
                        <button
                            onClick={() => setLangOpen((v) => !v)}
                            aria-label={t("lang.label")}
                            data-testid="lang-toggle"
                            className="h-10 px-3 grid grid-cols-[16px_auto] items-center gap-2 rounded-full border border-gold/30 text-gold hover:bg-gold/10 transition-colors text-xs tracking-widest uppercase"
                        >
                            <Globe className="h-4 w-4" />
                            <span>{lang === "hi" ? "हिन्दी" : "EN"}</span>
                        </button>
                        {langOpen && (
                            <div className="absolute right-0 mt-2 w-36 rounded-xl border border-gold/30 bg-ink-deep/95 backdrop-blur-xl py-1 shadow-xl z-50" data-testid="lang-menu">
                                <button
                                    onClick={() => pickLang("en")}
                                    data-testid="lang-option-en"
                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gold/10 ${lang === "en" ? "text-gold" : "text-cream"}`}
                                >
                                    English
                                </button>
                                <button
                                    onClick={() => pickLang("hi")}
                                    data-testid="lang-option-hi"
                                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gold/10 ${lang === "hi" ? "text-gold" : "text-cream"}`}
                                >
                                    हिन्दी
                                </button>
                            </div>
                        )}
                    </div>

                    <button
                        onClick={toggle}
                        aria-label="Toggle theme"
                        data-testid="theme-toggle"
                        className="h-10 w-10 grid place-items-center rounded-full border border-gold/30 text-gold hover:bg-gold/10 transition-colors"
                    >
                        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </button>
                    <Link to="/booking" className="btn-gold text-sm" data-testid="navbar-book-btn">
                        {t("nav.book_consultation")}
                    </Link>
                </div>

                <div className="lg:hidden flex items-center ml-auto gap-2">
                    <button
                        onClick={() => pickLang(lang === "en" ? "hi" : "en")}
                        aria-label={t("lang.label")}
                        data-testid="lang-toggle-mobile"
                        className="h-9 px-3 grid grid-cols-[14px_auto] items-center gap-1.5 rounded-full border border-gold/30 text-gold text-[11px] tracking-widest uppercase"
                    >
                        <Globe className="h-3.5 w-3.5" />
                        <span>{lang === "hi" ? "हि" : "EN"}</span>
                    </button>
                    <button
                        onClick={toggle}
                        aria-label="Toggle theme"
                        data-testid="theme-toggle-mobile"
                        className="h-9 w-9 grid place-items-center rounded-full border border-gold/30 text-gold"
                    >
                        {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
                    </button>
                    <button
                        onClick={() => setOpen((v) => !v)}
                        className="p-2 text-gold"
                        data-testid="mobile-menu-toggle"
                        aria-label="Toggle menu"
                    >
                        {open ? <X /> : <Menu />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="lg:hidden bg-ink/95 backdrop-blur-xl border-t border-gold/10">
                    <div className="container-vv py-6 flex flex-col gap-4">
                        {links.map((l) => (
                            <Link key={l.key} to={l.to} className="text-cream py-2 hover:text-gold" data-testid={`mobile-nav-${l.key}`}>
                                {l.label}
                            </Link>
                        ))}
                        <Link to="/booking" className="btn-gold mt-2 self-start" data-testid="mobile-book-btn">
                            {t("nav.book_consultation")}
                        </Link>
                    </div>
                </div>
            )}
        </header>
    );
}
